<template>
  <b-tab title="Faturamento" :active="isTheCurrentTab" :disabled="!isTheCurrentTab">
    <b-row>
      <!-- LADO ESQUERDO: CONFIGURAÇÕES -->
      <b-col md="7">
        <div class="card card-custom shadow-sm mb-6">
          <div class="card-header d-flex align-items-center justify-content-between py-3">
            <div class="d-flex align-items-center">
              <i class="la la-credit-card-alt mr-2 icon-2x text-primary"></i>
              <div>
                <div class="font-weight-bolder h5 mb-0">
                  Configurações de Cobrança
                </div>
                <small class="text-muted">Defina taxa de implantação e o plano de contas.</small>
              </div>
            </div>
          </div>

          <div class="card-body">
            <b-row>
              <!-- DIA DA COBRANÇA -->
              <b-col md="3" class="mb-4">
                <label>Dia da Cobrança no mês</label>
                <b-form-group label-for="dia_cobranca">
                  <b-form-select id="dia_cobranca" v-model="form.dia_cobranca" :options="diasDoMes" />
                </b-form-group>
              </b-col>

              <b-col md="3" class="mb-4">
                <label>Pular finais semana</label>
                <span class="switch switch-icon">
                  <label v-b-tooltip.hover.right="'Não/Sim'">
                    <input type="checkbox" v-model="form.pular_final_semana" />
                    <span></span>
                  </label>
                </span>
              </b-col>

              <!-- TAXA DE IMPLANTAÇÃO -->
              <b-col md="6" class="mb-4" v-if="currentEmpresaId == 1">
                <b-form-group label="Taxa de implantação" label-for="taxa_implantacao">
                  <b-input-group>
                    <b-input-group-prepend is-text>R$</b-input-group-prepend>
                    <money class="form-control text-right" id="taxa_implantacao" name="taxa_implantacao"
                      v-model="form.taxa_implantacao" v-bind="configMask" v-b-tooltip.hover
                      :title="'Valor cobrado na ativação do contrato (opcional)'" />
                  </b-input-group>

                  <b-alert :show="Number(form.taxa_implantacao || 0) > 0" fade variant="light"
                    class="mt-2 mb-0 py-2 px-3 border rounded w-100">
                    <div class="d-flex align-items-center flex-wrap">
                      <i class="la la-info-circle mr-2"></i>
                      <span class="mr-2">
                        Esta taxa será somada ao total do contrato.
                      </span>

                      <b-badge :variant="form.gerar_boleto_taxa ? 'success' : 'secondary'
                        " class="ml-sm-2 mt-2 mt-sm-0">
                        Boleto:
                        {{
                          form.gerar_boleto_taxa
                            ? "será gerado"
                            : "não será gerado"
                        }}
                      </b-badge>
                    </div>

                    <div class="mt-2">
                      <b-button size="sm" :variant="form.gerar_boleto_taxa
                        ? 'outline-danger'
                        : 'outline-success'
                        " class="text-nowrap" @click="
                          form.gerar_boleto_taxa = !form.gerar_boleto_taxa
                          ">
                        {{
                          form.gerar_boleto_taxa
                            ? "Não gerar boleto da taxa"
                            : "Gerar boleto da taxa"
                        }}
                      </b-button>
                    </div>
                  </b-alert>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- ===== PLUGINS ===== -->
            <b-row class="mt-4" v-if="currentEmpresaId == 1">
              <b-col cols="12">
                <h6 class="font-weight-bold text-muted mb-3">
                  Recursos adicionais
                </h6>
              </b-col>

              <!-- Certificado -->
              <b-col md="6">
                <div class="plugin-box">
                  <b-form-checkbox v-model="plugins.certificado.ativo" switch>
                    Certificado Digital A1
                  </b-form-checkbox>

                  <div class="text-muted mt-1">
                    R$ {{ numberToReal(plugins.certificado.valor, 2) }}
                  </div>

                  <small class="text-muted d-block mt-1">
                    Certificado para emissão fiscal.
                  </small>
                </div>
              </b-col>

              <!-- TEF -->
              <b-col md="6">
                <div class="plugin-box">
                  <b-form-checkbox v-model="plugins.tef.ativo" switch>
                    TEF Integrado
                  </b-form-checkbox>

                  <div class="text-muted mt-1">
                    R$ {{ numberToReal(plugins.tef.valor, 2) }}
                  </div>

                  <small class="text-muted d-block mt-1">
                    Integração com maquininhas e PDV.
                  </small>
                </div>
              </b-col>
            </b-row>

            <!-- PLANO DE CONTAS -->
            <b-row class="mt-4">
              <b-col md="6">
                <b-form-group label="Plano de Contas" label-for="plano_contas">
                  <VueSelect ref="buscar_planos" :filterable="false"
                    placeholder="Selecione um plano (ex.: Venda de Serviços)" :options="plano_contas"
                    v-model="planoSelecionado" @search="onSearchPlano" @input="setPlanoContas">
                    <template #option="{ descricao }">
                      <div class="d-center">{{ descricao }}</div>
                    </template>
                    <template #selected-option="{ descricao }">
                      <div class="selected d-center">{{ descricao }}</div>
                    </template>
                    <template #no-options>Nenhuma opção encontrada</template>
                  </VueSelect>
                </b-form-group>
              </b-col>

              <b-col sm="6" v-if="
                Number(form.taxa_implantacao || 0) > 0 &&
                currentEmpresaId == 1 &&
                form.gerar_boleto_taxa
              ">
                <label>Data do boleto referente taxa</label>
                <b-form-group id="data_boleto_taxa" label-for="Data pagamento da taxa">
                  <b-form-datepicker v-model="form.data_boleto_taxa" locale="pt-br" />
                </b-form-group>
              </b-col>
            </b-row>
          </div>
        </div>
      </b-col>

      <!-- LADO DIREITO: RESUMO (sticky) -->
      <b-col md="5">
        <div class="card card-custom shadow-sm sticky-top" style="top: 80px">
          <div class="card-header d-flex align-items-center py-3">
            <i class="la la-calculator mr-2 icon-2x text-success"></i>
            <div>
              <div class="font-weight-bolder h5 mb-0">Resumo Financeiro</div>
              <small class="text-muted">Visão geral do que falta pagar.</small>
            </div>
          </div>

          <div class="card-body">
            <ul class="list-unstyled mb-4">
              <li class="d-flex justify-content-between mb-2">
                <span class="text-muted" v-if="currentEmpresaId === 1">Plano</span>
                <span class="text-muted" v-else>Serviço</span>
                <span class="font-weight-600">
                  R$ {{ numberToReal(totalPlano, 2) }}
                </span>
              </li>

              <li class="d-flex justify-content-between mb-2" v-if="currentEmpresaId === 1">
                <span class="text-muted">Taxa de implantação</span>
                <span class="font-weight-600">
                  R$ {{ numberToReal(form.taxa_implantacao || 0, 2) }}
                </span>
              </li>

              <li class="d-flex justify-content-between align-items-center mb-2" v-if="plugins.certificado.ativo">
                <span class="text-muted">Certificado Digital</span>

                <div style="width: 140px">
                  <b-form-input type="number" step="0.01" min="0" class="text-right"
                    v-model.number="plugins.certificado.valor" />
                </div>
              </li>

              <li class="d-flex justify-content-between align-items-center mb-2" v-if="plugins.tef.ativo">
                <span class="text-muted">TEF Integrado</span>

                <div style="width: 140px">
                  <b-form-input type="number" step="0.01" min="0" class="text-right"
                    v-model.number="plugins.tef.valor" />

                </div>
              </li>

              <li class="d-flex justify-content-between pt-2 mt-2 border-top">
                <span class="font-weight-bolder">A PAGAR</span>
                <span class="font-weight-boldest h5 mb-0">
                  R$ {{ numberToReal(subtotal, 2) }}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- ===== COBRANÇA ANUAL + FORMA DE PAGAMENTO ===== -->
    <b-row v-if="currentEmpresaId == 1">
      <b-col md="2">
        <label>Cobrança Anual</label>
        <span class="switch switch-icon">
          <label v-b-tooltip.hover.right="'Não/Sim'">
            <input type="checkbox" v-model="form.cobranca_anual" />
            <span></span>
          </label>
        </span>
      </b-col>

      <b-row v-if="form.cobranca_anual" class="w-100">
        <b-col md="12">
          <b-alert variant="primary" show class="mt-2 d-flex align-items-start">
            <div class="mr-2">
              <i class="la la-info-circle" style="font-size: 1.25rem"></i>
            </div>
            <div>
              <div class="font-weight-bold mb-1">
                Ao ativar a cobrança anual, serão gerados os boletos conforme o
                período do contrato.
              </div>
              <div class="text-muted">
                Selecione a forma de pagamento e edite valores/datas, se
                necessário.
              </div>
            </div>
          </b-alert>
        </b-col>

        <!-- FORMA DE PAGAMENTO -->
        <b-col md="6">
          <b-form-group label="Forma de Pagamento" label-for="forma_pagamento">
            <VueSelect id="forma_pagamento" ref="buscar_forma_pagamento" :filterable="true" :options="formas_pagamento"
              :disabled="valorPagoARealizar <= 0" v-model="formaPagamento" @search="onSearchFormaPagamentos"
              @input="setFormaPagamento" placeholder="Selecione a forma de pagamento">
              <template #option="{ descricao }">
                <div class="d-center">{{ descricao }}</div>
              </template>
              <template #selected-option="{ descricao }">
                <div class="selected d-center">{{ descricao }}</div>
              </template>
              <template #no-options>Nenhuma forma encontrada</template>
            </VueSelect>

            <small v-if="valorPagoARealizar <= 0" class="text-muted">
              Nada a parcelar: total já quitado.
            </small>
          </b-form-group>
        </b-col>

        <b-table :key="pagamentoKey" :items="form.pagamentos?.range || []" :fields="btFields" small responsive bordered
          striped head-variant="light" show-empty empty-text="Selecione uma forma de pagamento para gerar as parcelas.">
          <template #cell(valor)="data">
            <span class="text-primary font-weight-bold cursor-pointer"
              @click="openEditValor(data.index, data.item.value)">
              {{ numberToReal(data.item.value, 2) }}
            </span>
          </template>

          <template #cell(vencimento)="data">
            <span class="text-muted cursor-pointer" @click="openEditVencimento(data.index, data.item.date)">
              {{ formatDate(data.item.date) }}
            </span>
          </template>
        </b-table>
      </b-row>
    </b-row>

    <!-- Modal: editar valor da parcela -->
    <b-modal id="modal-pagamento" title="Editar valor da parcela" ok-title="Salvar" cancel-title="Cancelar"
      @ok="saveEditValor" @hidden="resetPagamentoEdit">
      <b-form-group label="Valor (R$)">
        <b-input-group prepend="R$">
          <b-form-input v-model.number="pagamentoEditado" type="number" step="0.01" min="0" class="text-right" />
        </b-input-group>
        <small class="text-muted d-block mt-2">
          Dica: você pode editar mais de uma parcela; o restante é recalculado
          automaticamente.
        </small>
      </b-form-group>
    </b-modal>

    <!-- Modal: editar vencimento -->
    <b-modal id="modal-calendar" title="Selecionar vencimento" ok-title="Salvar" cancel-title="Cancelar"
      @ok="saveEditVencimento" @hidden="resetVencimentoEdit">
      <b-form-group label="Vencimento">
        <b-form-datepicker v-model="vencimentoSelected" locale="pt-br" :date-format-options="{
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }" />
      </b-form-group>
    </b-modal>

    <br />
  </b-tab>
</template>

<script>
import { mapGetters, mapState, mapActions } from "vuex";
import VueSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { validationMixin } from "vuelidate";
import FaturamentoData, { FaturamentoDataRequired } from "../FaturamentoData";
import {
  SistemaMixin,
  ValidacaoMixin,
  InstallmentsMixin,
} from "@/modules/Application";
import { mask } from "vue-the-mask";
import { Money } from "v-money";
import { MONEY as settingMoney } from "@/modules/Application";
import { store } from "../../storeContratos";
import _ from "lodash";

export default {
  name: "app-contratos-form-faturamentoTab",
  components: { VueSelect, Money },
  directives: { mask },
  mixins: [validationMixin, SistemaMixin, ValidacaoMixin, InstallmentsMixin],
  validations: { form: FaturamentoDataRequired },

  data() {
    return {
      store,
      form: {},
      configMask: settingMoney,
      plano_contas: [],
      planoConta: null,
      planoSelecionado: null,
      pagamentosRealizados: [],
      valorPago: 0,
      subtotal: 0,
      valorPagoARealizar: 0,
      formas_pagamento: [],
      formasPagamentoArray: [],
      formaPagamento: null,
      pagamentosEditados: [],
      selectedPagamento: null,
      pagamentoEditado: null,
      vencimentoSelected: null,
      blockNFSe: false,
      keyToReRender: 0,
      seq: { intervalo: 30 },
      pagamentoKey: 0,
      btFields: [
        { key: "id", label: "#", thStyle: { width: "60px" } },
        {
          key: "valor",
          label: "Valor",
          thClass: "text-right",
          tdClass: "text-right",
          thStyle: { width: "60px" },
          formatter: (v) => this.numberToReal(v, 2),
        },
        {
          key: "vencimento",
          label: "Vencimento",
          thClass: "text-right",
          tdClass: "text-right",
          thStyle: { width: "60px" },
          formatter: (v) => this.formatDate(v),
        },
      ],
      plugins: {
        certificado: {
          ativo: false,
          nome: "Certificado Digital A1",
          valor: 190,
        },
        tef: {
          ativo: false,
          nome: "TEF Integrado",
          valor: 1069.9,
        },
      },
    };
  },

  created() {
    if (Object.keys(this.$route.params || {}).length !== 0) {
      this.getDataInit();

      const {
        pagamentos,
        dia_cobranca,
        taxa_implantacao,
        plano_contas,
        cobranca_anual,
        considerar_taxa,
        data_boleto_taxa,
        gerar_boleto_taxa,
        pular_final_semana,
        plugin_certificado,
        plugin_tef,
        valor_tef,
        valor_certificado,
      } = this.$route.params.contratoData || {};

      this.form.pagamentos = pagamentos;
      this.form.dia_cobranca = dia_cobranca;
      this.form.taxa_implantacao = taxa_implantacao;
      this.form.cobranca_anual = cobranca_anual;
      this.planoSelecionado = plano_contas;
      this.form.considerar_taxa = considerar_taxa;
      this.form.data_boleto_taxa = data_boleto_taxa;
      this.form.gerar_boleto_taxa = gerar_boleto_taxa;
      this.form.pular_final_semana = pular_final_semana;

      this.plugins.certificado.ativo = !!plugin_certificado;
      this.plugins.tef.ativo = !!plugin_tef;
      this.plugins.certificado.valor = valor_certificado ?? 0;
      this.plugins.tef.valor = valor_tef ?? 0;

      this.pagamentosRealizados = Array.isArray(this.form.pagamentos)
        ? this.form.pagamentos
        : [];

      this.valorPago = this.pagamentosRealizados.reduce(
        (acc, p) => acc + Number(p?.value ?? 0),
        0
      );

      this.recalcularFinanceiro();
      this.syncPluginsToStore();
    } else {
      this.getDataInit();
    }
  },

  computed: {
    ...mapGetters([
      "isAuthenticated",
      "breadcrumbs",
      "pageTitle",
      "layoutConfig",
    ]),
    ...mapState(["loading"]),
    ...mapState({
      currentUser: (state) => state.auth.user,
    }),

    isLoading() {
      return this.loading.loading;
    },

    isTheCurrentTab() {
      return store.currentTab === 2;
    },

    totalPlano() {
      return this.store.formServicos.reduce(
        (acc, s) => acc + Number(s.total || 0),
        0
      );
    },

    currentEmpresaId() {
      return this.currentUser.usuario_empresa.id_empresa;
    },

    valorPagamento() {
      if (!Array.isArray(this.pagamento) || this.pagamento.length === 0)
        return 0;
      return this.pagamento[0].valor;
    },

    diasDoMes() {
      const EMPRESA_DIAS_RESTRITOS = {
        31: [5, 10],
      };

      const restritos = EMPRESA_DIAS_RESTRITOS[this.currentEmpresaId];

      const dias =
        Array.isArray(restritos) && restritos.length
          ? restritos.slice().sort((a, b) => a - b)
          : Array.from({ length: 31 }, (_, i) => i + 1);

      return dias.map((d) => ({ value: d, text: `${d}º dia do mês` }));
    },

    valorPlugins() {
      let total = 0;

      if (this.plugins.certificado.ativo) {
        total += Number(this.plugins.certificado.valor || 0);
      }

      if (this.plugins.tef.ativo) {
        total += Number(this.plugins.tef.valor || 0);
      }

      return total;
    },

    pluginsSelecionados() {
      const plugins = [];

      if (this.plugins.certificado.ativo) {
        plugins.push({
          key: "certificado",
          nome: this.plugins.certificado.nome,
          valor: this.plugins.certificado.valor,
        });
      }

      if (this.plugins.tef.ativo) {
        plugins.push({
          key: "tef",
          nome: this.plugins.tef.nome,
          valor: this.plugins.tef.valor,
        });
      }

      return plugins;
    },
  },

  watch: {
    "store.formServicos": {
      handler() {
        this.valorPago = this.pagamentosRealizados.reduce(
          (acc, pagamento) => acc + Number(pagamento?.value ?? 0),
          0
        );
        this.recalcularFinanceiro();
      },
      deep: true,
    },

    "form.taxa_implantacao"(novo) {
      const taxa = Number(novo || 0);

      this.valorPagoARealizar = this.getValorParcelavel() - this.valorPago;
      this.subtotal = this.totalPlano + taxa + this.valorPlugins - this.valorPago;
    },

    form: {
      handler(newValue) {
        this.store.formFaturamento = {
          ...newValue,
          plugin_certificado: this.plugins.certificado.ativo,
          plugin_tef: this.plugins.tef.ativo,
          valor_tef: Number(this.plugins.tef.valor || 0),
          valor_certificado: Number(this.plugins.certificado.valor || 0),
          plugins: this.pluginsSelecionados,
        };
      },
      deep: true,
    },

    plugins: {
      deep: true,
      handler() {
        this.recalcularFinanceiro();
        this.syncPluginsToStore();

        if (this.formaPagamento) {
          this.setFormaPagamento(this.formaPagamento);
        }
      },
    },

    planoConta(newValue, oldValue) {
      if (JSON.stringify(newValue) === JSON.stringify(oldValue)) return;
      this.planoContasFilled = !!newValue;
      this.store.formFaturamento.plano_conta = newValue;
    },
  },

  methods: {
    ...mapActions(["setLoading"]),

    async getDataInit() {
      this.form = new FaturamentoData({});
      this.plano_contas = await this.getPlanoContas(2);

      this.formas_pagamento = await this.getFormasPagamentos();
      this.formasPagamentoArray = Array.isArray(this.formas_pagamento)
        ? [...this.formas_pagamento]
        : [];

      this.form.data_boleto_taxa = this.thisDate();
      this.planoConta =
        this.plano_contas.find((p) => p.descricao === "Venda de Serviços") ||
        null;

      this.pagamentosRealizados = Array.isArray(this.form.pagamentos)
        ? this.form.pagamentos
        : [];

      this.valorPago = this.pagamentosRealizados.reduce(
        (acc, p) => acc + Number(p?.value ?? 0),
        0
      );

      this.recalcularFinanceiro();
      this.syncPluginsToStore();
    },

    getValorParcelavel() {
      return this.totalPlano + this.valorPlugins;
    },

    recalcularFinanceiro() {
      this.valorPagoARealizar = this.getValorParcelavel() - this.valorPago;

      this.subtotal =
        this.totalPlano +
        Number(this.form.taxa_implantacao || 0) +
        this.valorPlugins -
        this.valorPago;
    },

    syncPluginsToStore() {
      this.store.formFaturamento.plugins = this.pluginsSelecionados;
      this.store.formFaturamento.plugin_certificado =
        this.plugins.certificado.ativo;
      this.store.formFaturamento.plugin_tef = this.plugins.tef.ativo;
      this.store.formFaturamento.valor_certificado = Number(
        this.plugins.certificado.valor || 0
      );
      this.store.formFaturamento.valor_tef = Number(
        this.plugins.tef.valor || 0
      );
    },

    openEditValor(index, valorAtual) {
      this.selectedPagamento = index;
      this.pagamentoEditado = Number(valorAtual) || 0;
      this.$bvModal.show("modal-pagamento");
    },

    openEditVencimento(index, vencimentoAtual) {
      this.selectedPagamento = index;
      this.vencimentoSelected = vencimentoAtual || null;
      this.$bvModal.show("modal-calendar");
    },

    resetPagamentoEdit() {
      this.selectedPagamento = null;
      this.pagamentoEditado = null;
    },

    resetVencimentoEdit() {
      this.selectedPagamento = null;
      this.vencimentoSelected = null;
    },

    saveEditValor() {
      if (this.selectedPagamento == null) return;

      this.$set(
        this.form.pagamentos.range[this.selectedPagamento],
        "value",
        Number(this.pagamentoEditado) || 0
      );

      const iExist = this.pagamentosEditados.findIndex(
        (p) => p.index === this.selectedPagamento
      );

      const reg = {
        index: this.selectedPagamento,
        value: Number(this.pagamentoEditado) || 0,
      };

      if (iExist === -1) {
        this.pagamentosEditados.push(reg);
      } else {
        this.pagamentosEditados.splice(iExist, 1, reg);
      }

      const editTotal = this.pagamentosEditados.reduce(
        (acc, p) => acc + Number(p?.value || 0),
        0
      );

      const restante = this.getValorParcelavel() - editTotal;

      const qtdRest = Math.max(
        (this.form.pagamentos.range.length || 0) -
          this.pagamentosEditados.length,
        0
      );

      const valorRest =
        qtdRest > 0 ? Math.round((restante / qtdRest) * 100) / 100 : 0;

      this.form.pagamentos.range = this.form.pagamentos.range.map((it, idx) => {
        const edit = this.pagamentosEditados.find((p) => p.index === idx);
        return { ...it, value: edit ? edit.value : valorRest };
      });

      this.pagamentoKey++;
    },

    saveEditVencimento() {
      if (this.selectedPagamento == null) return;

      this.$set(
        this.form.pagamentos.range[this.selectedPagamento],
        "date",
        this.vencimentoSelected
      );

      this.pagamentoKey++;
    },

    setFormaPagamento(forma_pagamento) {
      if (!forma_pagamento) {
        this.formaPagamento = null;
        this.form.pagamentos = { range: [] };
        this.pagamentosEditados = [];
        this.pagamentoKey++;
        return;
      }

      const aPagar = this.getValorParcelavel() - Number(this.valorPago || 0);

      const range = this.calculateInstallments(forma_pagamento, aPagar).map(
        (el, idx) => ({
          id: idx + 1,
          date: el.date,
          value: Number(el.value) || 0,
        })
      );

      this.form.pagamentos = {
        id_forma_pagamento: forma_pagamento.id,
        id_meio_pagamento: forma_pagamento.id_meio_pagamento,
        descricao: forma_pagamento.descricao,
        tipo_repeticao: forma_pagamento.tipo_repeticao,
        vezes: forma_pagamento.vezes,
        intervalo_dias: forma_pagamento.intervalo_dias ?? null,
        range,
      };

      this.vencimentoSelected = range[0]?.date || null;
      this.formaPagamento = forma_pagamento;
      this.pagamentosEditados = [];
      this.pagamentoKey++;
    },

    changePagamento(pagamentoIndex) {
      this.selectedPagamento = pagamentoIndex;
    },

    closeModalPagamento() {
      this.openChangePagamentoModal = false;
    },

    changePagamentoEdited(valor) {
      this.pagamentoEditado = valor;
    },

    onSearchFormaPagamentos(search, loading) {
      loading(true);

      if (!search?.length) {
        this.formas_pagamento = [...this.formasPagamentoArray];
        loading(false);
        return;
      }

      this.searchFormaPagamentos(search, loading, this);
    },

    searchFormaPagamentos: _.debounce(async (search, loading, vm) => {
      const termo = (search || "").toLowerCase();

      vm.formas_pagamento = vm.formasPagamentoArray.filter((item) =>
        (item.descricao || "").toLowerCase().includes(termo)
      );

      loading(false);
    }, 300),

    numberToReal(v, dec = 2) {
      const n = Number(v || 0);

      return n.toLocaleString("pt-BR", {
        style: "decimal",
        minimumFractionDigits: dec,
        maximumFractionDigits: dec,
      });
    },

    validateFaturamentoState() {
      return null;
    },

    setPlanoContas(planoContas) {
      this.planoSelecionado = planoContas;
      this.form.id_plano_contas = planoContas.id;
      this.store.formFaturamento.plano_conta = planoContas;
    },

    onSearchPlano(search, loading) {
      if (search.length) {
        loading(true);
        this.searchPlano(loading, search, this);
      }
    },

    searchPlano: _.debounce(async (loading, search, vm) => {
      vm.plano_contas = await vm.getPlanoContasContaReceber(search);
      loading(false);
    }, 450),
  },
};
</script>

<style scoped>
.hasErrors .vs__dropdown-toggle {
  border: 2px solid #f64e60;
}

.hasErrors .vs__clear,
.hasErrors .vs__open-indicator {
  fill: #f64e60;
}

.inputFilled .vs__dropdown-toggle {
  border: 2px solid #1bc5bd;
}

.inputFilled .vs__clear,
.inputFilled .vs__open-indicator {
  fill: #1bc5bd;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.card-header .icon-2x {
  font-size: 1.6rem;
}

.font-weight-600 {
  font-weight: 600;
}

.font-weight-800 {
  font-weight: 800;
}

.cursor-pointer {
  cursor: pointer;
}

.card-body .alert {
  overflow: hidden;
}

.text-nowrap {
  white-space: nowrap;
}

.plugin-box {
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 14px;
  background: #fafafa;
  transition: all 0.2s;
}

.plugin-box:hover {
  border-color: #ff6b3d;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
}
</style>